'use client';

import { useState, useEffect, useMemo } from 'react';
import { Menu, X, ShoppingBag, Search, Heart, User, ChevronDown, Check } from 'lucide-react';
import { useCart } from '@/lib/cart-store';
import { useWishlist } from '@/lib/wishlist-store';
import { useAuth } from '@/lib/auth-store';
import { useI18n, useTranslation, LOCALES, Locale } from '@/lib/i18n';
import { PRODUCTS } from '@/lib/products';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const { openCart, getCount } = useCart();
  const { openWishlist, getCount: getWishCount } = useWishlist();
  const { user, openAuth } = useAuth();
  const { locale, setLocale } = useI18n();
  const { t } = useTranslation();
  const count = getCount();
  const wishCount = getWishCount();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const NAV_LINKS = [
    { href: '#atelier', label: t('nav.atelier') },
    { href: '#a-propos', label: t('nav.about') },
    { href: '#exception', label: t('nav.exception') },
    { href: '#boutique-shop', label: t('nav.boutique') },
    { href: '#galerie', label: t('nav.galerie') },
    { href: '#tracabilite', label: t('nav.tracabilite') },
    { href: '#sur-mesure', label: t('nav.surmesure') },
    { href: '#chroniques', label: t('nav.chroniques') },
    { href: '#temoignages', label: t('nav.temoignages') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const results = useMemo(() => {
    if (query.trim().length < 2) return [];
    const q = query.toLowerCase();
    return PRODUCTS.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [query]);

  const currentLocale = LOCALES.find((l) => l.code === locale) || LOCALES[0];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0a0a0f]/95 backdrop-blur-md border-b border-[#D4AF37]/20 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-3">
        {/* Logo */}
        <a href="#top" className="group flex flex-col items-start leading-none flex-shrink-0">
          <span className="font-display text-[#D4AF37] text-lg sm:text-xl font-semibold tracking-[0.18em]">
            MATAR
          </span>
          <span className="text-[10px] sm:text-[11px] tracking-[0.4em] text-[#F4E8C1]/70 mt-0.5 font-light">
            STYLE CUIR
          </span>
        </a>

        {/* Desktop nav */}
        <ul className="hidden 2xl:flex items-center gap-4">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="relative text-[10px] uppercase tracking-[0.18em] text-[#F4E8C1]/80 hover:text-[#D4AF37] transition-colors duration-300 font-medium after:absolute after:-bottom-1.5 after:left-0 after:w-0 after:h-px after:bg-[#D4AF37] after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right side */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-all text-[#D4AF37] text-[10px] uppercase tracking-[0.15em] font-semibold"
              aria-label="Changer de langue"
            >
              <span className="text-base leading-none">{currentLocale.flag}</span>
              <span className="hidden sm:inline">{currentLocale.code.toUpperCase()}</span>
              <ChevronDown size={10} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>

            {langOpen && (
              <div className="absolute top-12 right-0 w-44 bg-[#0a0a0f] border border-[#D4AF37]/30 shadow-2xl z-50">
                {LOCALES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLocale(l.code as Locale);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#12121a] transition-colors border-b border-[#D4AF37]/10 last:border-0 ${
                      l.code === locale ? 'bg-[#12121a]/60' : ''
                    }`}
                  >
                    <span className="text-base leading-none">{l.flag}</span>
                    <span className="text-xs text-[#F4E8C1] flex-1 text-left font-medium">
                      {l.label}
                    </span>
                    {l.code === locale && <Check size={12} className="text-[#D4AF37]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-gold-gradient hover:text-[#0a0a0f] hover:border-[#D4AF37] transition-all"
              aria-label={t('nav.recherche')}
            >
              <Search size={15} />
            </button>

            {searchOpen && (
              <div className="absolute top-12 right-0 w-80 sm:w-96 bg-[#0a0a0f] border border-[#D4AF37]/30 shadow-2xl">
                <div className="p-4 border-b border-[#D4AF37]/15">
                  <input
                    autoFocus
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('common.search_placeholder')}
                    className="w-full bg-[#12121a] border border-[#D4AF37]/25 px-4 py-2.5 text-sm text-[#F4E8C1] placeholder:text-[#F4E8C1]/35 focus:border-[#D4AF37] focus:outline-none transition-colors"
                  />
                </div>

                {results.length > 0 && (
                  <ul className="max-h-96 overflow-y-auto">
                    {results.map((p) => (
                      <li key={p.id}>
                        <a
                          href="#boutique-shop"
                          onClick={() => {
                            setSearchOpen(false);
                            setQuery('');
                          }}
                          className="flex items-center gap-3 p-3 hover:bg-[#12121a] transition-colors border-b border-[#D4AF37]/10"
                        >
                          <img src={p.image} alt={p.name} className="w-12 h-12 object-cover border border-[#D4AF37]/20 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85">{p.category}</div>
                            <div className="text-sm font-medium text-[#F4E8C1] truncate">{p.name}</div>
                          </div>
                          <div className="font-display text-sm font-bold text-gold-gradient flex-shrink-0">
                            {p.price.toLocaleString('fr-BE')} €
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}

                {query.trim().length >= 2 && results.length === 0 && (
                  <div className="p-6 text-center">
                    <p className="text-xs text-[#F4E8C1]/55 italic font-serif">
                      {t('common.no_results')} « {query} »
                    </p>
                  </div>
                )}

                {query.trim().length < 2 && (
                  <div className="p-6 text-center">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/45">
                      {t('common.type_min')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Account */}
          <button
            onClick={() => openAuth(user ? 'profile' : 'login')}
            className={`w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center border transition-all ${
              user
                ? 'bg-gold-gradient text-[#0a0a0f] border-[#D4AF37]'
                : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-gold-gradient hover:text-[#0a0a0f] hover:border-[#D4AF37]'
            }`}
            aria-label={t('nav.compte')}
          >
            <User size={15} />
          </button>

          {/* Wishlist */}
          <button
            onClick={openWishlist}
            className="relative w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#CE1126] hover:text-white hover:border-[#CE1126] transition-all"
            aria-label={t('nav.favoris')}
          >
            <Heart size={15} />
            {wishCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#D4AF37] text-[#0a0a0f] text-[9px] font-bold flex items-center justify-center border-2 border-[#0a0a0f]">
                {wishCount > 9 ? '9+' : wishCount}
              </span>
            )}
          </button>

          {/* Cart */}
          <button
            onClick={openCart}
            className="relative flex items-center gap-2 px-2 sm:px-4 py-2 border border-[#D4AF37]/40 hover:bg-gold-gradient hover:text-[#0a0a0f] hover:border-[#D4AF37] transition-all duration-400 group"
            aria-label={t('nav.panier')}
          >
            <ShoppingBag size={15} className="text-[#D4AF37] group-hover:text-[#0a0a0f]" />
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] font-semibold text-[#F4E8C1] group-hover:text-[#0a0a0f]">
              {t('nav.panier')}
            </span>
            {count > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#CE1126] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0a0a0f]">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="2xl:hidden text-[#D4AF37] p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="2xl:hidden bg-[#0a0a0f]/98 backdrop-blur-md border-t border-[#D4AF37]/15 mt-3">
          <ul className="flex flex-col px-5 py-4 gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block py-2.5 text-sm uppercase tracking-[0.18em] text-[#F4E8C1]/85 hover:text-[#D4AF37] transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
